/**
 *
 * Default NIfTI loading: auto-call disk_items API -> match -> load on mount.
 */

import { useEffect, useRef, useState, forwardRef, Ref } from "react";
import { EVENTS } from "@cornerstonejs/core";
import { Enums } from "@cornerstonejs/tools";
import { initViewer } from "lib/cornerstone/setupCornerstoneViewer";
import { useSnapshot } from "valtio";
import settingsState from "stores/settings";
import uiState, { toggleExpandedPane } from "stores/ui";
import {
  crosshairRenderingListener,
  voiModifiedListener,
} from "lib/cornerstone/helpers/listeners";
import { useNotify } from "react-admin";
import viewerState, { buildNiftiMetadata } from "stores/viewer";
import useViewerParams from "shared/hooks/useViewerParams";

import {
  discoverNiftiFiles,
  type DiscoveryResult,
} from "../../../../lib/cornerstone/helpers/niftiDiscovery";
import { ENTRYPOINT } from "config/api";

export interface ViewerProps {
  id: string;
}
let lastLoadedAcq: string | null = null;

type ViewerPhase = "idle" | "discovering" | "loading" | "ready" | "error";

function Viewer({ id }: ViewerProps) {
  const ui = useSnapshot(uiState);
  const snap = useSnapshot(viewerState);
  const notify = useNotify();
  const { patient_id, acquisition } = useViewerParams();
  const viewerRef1 = useRef<HTMLDivElement>(null);
  const viewerRef2 = useRef<HTMLDivElement>(null);
  const viewerRef3 = useRef<HTMLDivElement>(null);

  const [discovery, setDiscovery] = useState<DiscoveryResult | null>(null);
  const [phase, setPhase] = useState<ViewerPhase>("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  if (ui.error instanceof Error) {
    throw ui.error;
  }

  useEffect(() => {
    const viewer1 = viewerRef1.current;
    if (!viewer1) return;
    viewer1.addEventListener(
      Enums.Events.ANNOTATION_RENDERED,
      crosshairRenderingListener,
    );
    viewer1.addEventListener(EVENTS.VOI_MODIFIED, voiModifiedListener);
    return () => {
      viewer1.removeEventListener(
        Enums.Events.ANNOTATION_RENDERED,
        crosshairRenderingListener,
      );
      viewer1.removeEventListener(EVENTS.VOI_MODIFIED, voiModifiedListener);
    };
  });

  // Auto-discover + auto-load
  useEffect(() => {
    if (!patient_id || !acquisition) return;
    // ★ Guard: don't re-discover if already loaded for this patient
    if (
      lastLoadedAcq === acquisition &&
      viewerState.viewerId &&
      Object.keys(viewerState.niftiBases ?? {}).length > 0
    ) {
      return;
    }
    lastLoadedAcq = acquisition;
    let cancelled = false;
    // Reset viewer state to prevent stale data from previous patient
    viewerState.instanceMetadata = null;
    viewerState.segmentsPerSeries = [];
    viewerState.niftiDatasets = [];
    viewerState.niftiBases = {};
    viewerState.niftiActiveModality = null;

    async function autoDiscoverAndLoad() {
      setPhase("discovering");
      setErrorMsg(null);

      try {
        const result = await discoverNiftiFiles(
          ENTRYPOINT,
          patient_id,
          acquisition,
        );
        if (cancelled) return;

        setDiscovery(result);
        if (result.bases.length === 0) {
          throw new Error(
            `No NIfTI base images found (${result.allFiles.length} files matched total)`,
          );
        }

        // save all base URLs for later modality switching in displaySegment
        const basesMap: Record<string, string> = {};
        for (const b of result.bases) {
          if (b.entry.modality) basesMap[b.entry.modality] = b.url;
        }
        viewerState.niftiBases = basesMap;

        const mod = result.activeModality;

        viewerState.niftiActiveModality = mod;

        const base =
          result.bases.find((b) => b.entry.modality === mod) ?? result.bases[0];

        const segUrls: Record<string, string> = {};
        for (const seg of result.segmentations) {
          segUrls[seg.entry.setType] = seg.url;
        }

        setPhase("loading");
        viewerState.instanceMetadata = buildNiftiMetadata(base.url, {
          studyId: `${result.infos.subject}_${acquisition}`,
          segUrls: Object.keys(segUrls).length > 0 ? segUrls : undefined,
          modality: mod,
        }) as any;
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : String(e);
        setErrorMsg(msg);
        setPhase("error");
        console.error("[Viewer] Auto-discovery failed:", e);
      }
    }

    autoDiscoverAndLoad();
    return () => {
      cancelled = true;
    };
  }, [patient_id, acquisition]);

  // instanceMetadata change -> initViewer
  useEffect(() => {
    const rawMeta = viewerState.instanceMetadata;
    if (!rawMeta) return;

    // ★ Guard: don't re-init if already initialized
    if (viewerState.viewerId === id && phase === "ready") {
      return;
    }
    uiState.loading = true;

    initViewer(id, [viewerRef1, viewerRef2, viewerRef3], rawMeta, notify)
      .then(() => setPhase("ready"))
      .catch((e) => {
        uiState.error = e;
        setPhase("error");
        setErrorMsg(e instanceof Error ? e.message : String(e));
      })
      .finally(() => (uiState.loading = false));

    uiState.error = undefined;
  }, [snap.instanceMetadata, id, notify]);

  return (
    <>
      <ViewerPane
        id={`viewer_${id}_1`}
        orientationLabels={{ top: "S", left: "R" }}
        ref={viewerRef1}
        expandedPane={ui.expandedPane}
      />
      <ViewerPane
        id={`viewer_${id}_2`}
        orientationLabels={{ top: "S", left: "A" }}
        ref={viewerRef2}
        expandedPane={ui.expandedPane}
      />
      <ViewerPane
        id={`viewer_${id}_3`}
        orientationLabels={{ top: "A", left: "R" }}
        ref={viewerRef3}
        expandedPane={ui.expandedPane}
      />
    </>
  );
}

interface ViewerPaneProps {
  id: string;
  orientationLabels: { top: string; left: string };
  expandedPane: string | null;
}

export const ViewerPane = forwardRef(function ViewerPane(
  { id, orientationLabels, expandedPane }: ViewerPaneProps,
  ref: Ref<HTMLDivElement>,
) {
  let className = "h-[45vh] rounded-lg overflow-hidden bg-black relative";
  if (expandedPane === id) {
    className =
      "h-[90vh] w-[85vw] rounded-lg overflow-hidden bg-black relative";
  } else if (expandedPane !== null) {
    className = "hidden";
  }

  return (
    <div
      id={id}
      data-testid={id}
      onContextMenu={(e) => e.preventDefault()}
      onDoubleClick={() => toggleExpandedPane(id)}
      className={className}
      ref={ref}
    >
      <OrientationLabel
        top={orientationLabels.top}
        left={orientationLabels.left}
      />
    </div>
  );
});

function OrientationLabel({ top, left }: { top: string; left: string }) {
  const snap = useSnapshot(settingsState);
  if (!snap.showOrientationLabel) return null;

  return (
    <>
      <span className="absolute top-1 left-[49.3%] z-10">{top}</span>
      <span className="absolute top-[47.5%] left-1 z-10">{left}</span>
    </>
  );
}

export default Viewer;
